import React, {useEffect, useMemo, useState} from 'react';
import {Loader, Select, Text} from '@gravity-ui/uikit';
import {ActionModal} from '../../../../shared';
import {useCategoryGroupsWithCategories} from '../../../../entities';
import styles from './SelectCategoriesByGroupsModal.module.scss';

interface SelectCategoriesByGroupsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedCategoryIds: string[];
  onApply: (categoryIds: string[]) => void;
}

export const SelectCategoriesByGroupsModal = ({
  open,
  setOpen,
  selectedCategoryIds,
  onApply,
}: SelectCategoriesByGroupsModalProps) => {
  const {data: groups, isLoading} = useCategoryGroupsWithCategories({enabled: open});

  const [selectedByGroup, setSelectedByGroup] = useState<Record<string, string[]>>({});

  const groupsByCategoryId = useMemo(() => {
    const map: Record<string, string> = {};
    groups?.forEach((group) => {
      group.categories.forEach((category) => {
        map[category.id] = group.id;
      });
    });
    return map;
  }, [groups]);

  useEffect(() => {
    if (!open || !groups) {
      return;
    }
    const initialState: Record<string, string[]> = {};
    groups.forEach((group) => {
      initialState[group.id] = [];
    });
    selectedCategoryIds.forEach((id) => {
      const groupId = groupsByCategoryId[id];
      if (groupId) {
        initialState[groupId] = [...(initialState[groupId] || []), id];
      }
    });
    setSelectedByGroup(initialState);
  }, [open, groups, selectedCategoryIds, groupsByCategoryId]);

  const handleGroupChange = (groupId: string, value: string[]) => {
    setSelectedByGroup((prev) => ({
      ...prev,
      [groupId]: value,
    }));
  };

  const handleApply = () => {
    const ids = Object.values(selectedByGroup).flat();
    onApply(ids);
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <ActionModal
      open={open}
      setOpen={handleClose}
      onClose={handleClose}
      submitForm={handleApply}
      size="m"
      buttonsSize="l"
      caption="Выбрать категории"
      textButtonApply="Сохранить"
      textButtonCancel="Отменить"
      isDisabled={isLoading}>
      {isLoading ? (
        <div className={styles.loaderWrapper}>
          <Loader size="m" />
        </div>
      ) : (
        <div className={styles.groupsList}>
          {(groups || []).map((group) => {
            const options = group.categories.map((category) => ({
              value: category.id,
              content: category.name,
            }));
            return (
              <div key={group.id} className={styles.groupRow}>
                <Text variant="subheader-1" className={styles.groupLabel}>
                  {group.name}
                </Text>
                <Select
                  className={styles.groupSelect}
                  size="m"
                  options={options}
                  value={selectedByGroup[group.id] || []}
                  onUpdate={(value) => handleGroupChange(group.id, value)}
                  placeholder="Выберите категории"
                  multiple
                  filterable
                  filterPlaceholder="Поиск"
                  disabled={options.length === 0}
                />
              </div>
            );
          })}
          {(groups || []).length === 0 && (
            <Text variant="body-1" color="secondary">
              Нет доступных групп категорий
            </Text>
          )}
        </div>
      )}
    </ActionModal>
  );
};
